import { identity, isEmpty } from 'ramda'
import spacetime from 'spacetime'

import { OrgUsersById, User } from 'context/orgUsersContext'
import {
  Activity,
  ActivityContext,
  ActivityForContact,
  ActivityType,
  ScoredActivity,
  ScoredActivityType
} from 'modules/accountActivity/domain'

const getAgentDisplayName = (user: User | undefined) => {
  return user?.name || user?.email
}

export const participantsToString = (
  activityContext: ActivityContext,
  orgUsersById: OrgUsersById
) => {
  return activityContext?.agentParticipants
    ?.map(id => getAgentDisplayName(orgUsersById[id]))
    .filter(identity)
    .join(', ')
}

export const activityTypeToStringDescription = (activity: Activity | ActivityForContact) => {
  if (activity.activityType === ActivityType.PAGE_VIEW) {
    return 'Site visit'
  }
  if (activity.activityType === ActivityType.MEETING_BOOKED) {
    return 'Meeting booked'
  }
  if (activity.activityType === ActivityType.MEETING_CANCELED) {
    return 'Meeting canceled'
  }
  if (activity.activityType === ActivityType.CONVERSATION) {
    return 'Conversation'
  }
  if (activity.activityType === ActivityType.EMAIL_CLICK) {
    return 'Email click'
  }
  if (activity.activityType === ActivityType.EMAIL_OPEN) {
    return 'Email open'
  }
  if (activity.activityType === ActivityType.EMAIL_SEND) {
    return 'Email send'
  }
  if (activity.activityType === ActivityType.VIDEO) {
    return 'Video watched'
  }
  if (activity.activityType === ActivityType.END_USER_IDENTIFIED) {
    return 'Email address provided'
  }
}

// these two are going away or will look very different soon

const isRecentUserActivity = (activity: ScoredActivity) => {
  const {
    diff: { years, months, days, hours, minutes }
  } = spacetime.now().since(activity.createdAt)

  return minutes < 4 && years === 0 && months === 0 && days === 0 && hours === 0 // there is no science behind this number. plan is to replace this with presence data.
}

export const userActivityTypeToWord = (activity: ScoredActivity, orgUsersById: OrgUsersById) => {
  if (
    activity.activityType
      .toLowerCase()
      .split('_')
      .some(p => p === 'view')
  ) {
    return isRecentUserActivity(activity) ? 'is viewing' : 'viewed'
  }
  if (activity.activityType === ScoredActivityType.MEETING_BOOKED) {
    const participants = participantsToString(activity.activityContext, orgUsersById)
    return isEmpty(participants) ? `booked a meeting` : `booked a meeting with ${participants}`
  }
  if (activity.activityType === ScoredActivityType.MEETING_CANCELED) {
    const participants = participantsToString(activity.activityContext, orgUsersById)
    return isEmpty(participants) ? `canceled a meeting` : `canceled a meeting with ${participants}`
  }
  if (activity.activityType === ScoredActivityType.CONVERSATION) {
    return activity.activityContext.conversationPreview
      ? `said: ${activity.activityContext.conversationPreview}`
      : `started a conversation`
  }
  if (activity.activityType === ScoredActivityType.EMAIL_CLICK) {
    return activity.activityContext.subject
      ? `clicked link in email “${activity.activityContext.subject}”`
      : `clicked link in email`
  }
  if (activity.activityType === ScoredActivityType.EMAIL_OPEN) {
    return activity.activityContext.subject
      ? `opened email “${activity.activityContext.subject}”`
      : `opened email`
  }
  if (activity.activityType === ScoredActivityType.VIDEO) {
    return isRecentUserActivity(activity)
      ? `is watching "${activity.activityContext.title}"`
      : `watched "${activity.activityContext.title}"`
  }
  if (activity.activityType === ScoredActivityType.END_USER_IDENTIFIED) {
    return 'provided their email'
  }
  return 'was active'
}

export const prefixUrlWithHttp = (link?: string): string | undefined => {
  if (!link) return link

  const VALID_PREFIXES = ['http://', 'https://', 'mailto:']

  for (const prefix of VALID_PREFIXES) {
    if (link.startsWith(prefix)) {
      return link
    }
  }

  return `http://${link}`
}
